import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  SubscriptionDetails,
  EnhancedResource,
  SecurityStep,
  TeamListItem,
} from "../../types";
import { useParentIframe } from "@trello-powerup-monorepo/iframe-messenger-v2";
import { SECURITY_STEPS, PAYWALL_MESSAGE_TYPES } from "../../constants";
import {
  calculateSecurityStepFromUnifiedState,
  isPaywallMessage,
} from "../logic";

export interface PaywallStateManagerContextValue {
  authStatus: "authenticated" | "pre-authenticated" | "unauthenticated";
  subscriptions: SubscriptionDetails[];
  resources: EnhancedResource[];
  ownedTeams: TeamListItem[];
  securityStep: SecurityStep;
  hasAccess: boolean;
}

const PaywallStateManagerContext =
  createContext<PaywallStateManagerContextValue | null>(null);

export interface PaywallStateManagerProviderProps {
  hasAccessClbck: (resources: EnhancedResource[]) => boolean;
  initialState?: PaywallStateManagerContextValue;
  onStateChange?: (state: PaywallStateManagerContextValue) => void;
}

export function usePaywallStateManagerProvider(): PaywallStateManagerContextValue {
  const context = useContext(PaywallStateManagerContext);
  if (!context) {
    throw new Error(
      "usePaywallStateManagerProvider must be used within PaywallStateManagerProvider"
    );
  }
  return context;
}

// Component that manages unified state inside route context
export function PaywallStateManagerProvider({
  children,
  hasAccessClbck,
  initialState,
  onStateChange,
}: React.PropsWithChildren<PaywallStateManagerProviderProps>) {
  const { onMessage, debug } = useParentIframe();

  const log = (message: string, context?: unknown) => {
    if (!debug) return;
    const debugFn = debug as (message: string, ...args: unknown[]) => void;
    const prefixed = `[PaywallParent] ${message}`;
    if (context !== undefined) {
      debugFn(prefixed, context);
    } else {
      debugFn(prefixed);
    }
  };

  const hasUnifiedStateRef = useRef(false);

  const [state, setState] = useState<PaywallStateManagerContextValue>(
    initialState ?? {
      authStatus: "unauthenticated",
      subscriptions: [],
      resources: [],
      ownedTeams: [],
      securityStep: SECURITY_STEPS.CHECKING_AUTH,
      hasAccess: false,
    }
  );

  useEffect(() => {
    const unsubscribe = onMessage((message) => {
      if (isPaywallMessage(message)) {
        // NEW: Handle unified state message (priority)
        if (message.type === PAYWALL_MESSAGE_TYPES.PAYWALL_STATE_UPDATED) {
          hasUnifiedStateRef.current = true;
          const { resources, authStatus, subscriptions, ownedTeams } =
            message.payload;
          // AIDEV-NOTE: Assuming, that resources array is never empty
          const hasResources = resources.length > 0;
          const hasAccess = hasAccessClbck(resources);

          log("PAYWALL_STATE_UPDATED received", {
            authStatus,
            resourcesCount: resources.length,
            subscriptionsCount: subscriptions.length,
            hasAccess,
          });

          const newSecurityStep = calculateSecurityStepFromUnifiedState(
            authStatus,
            hasResources,
            hasAccess,
            debug
          );

          // Update all related state atomically to prevent inconsistencies
          setState((prevState) => {
            // Prevent unnecessary updates if security step hasn't changed
            if (
              prevState.securityStep === newSecurityStep &&
              prevState.authStatus === authStatus &&
              !hasAccessLevelChanged(prevState, subscriptions)
            ) {
              log("Skipping unified state update; state unchanged", {
                securityStep: newSecurityStep,
                authStatus,
                subscriptions,
              });
              return prevState;
            }

            const shouldPreserveOptimisticState =
              !hasResources && prevState.resources.length > 0;

            if (shouldPreserveOptimisticState) {
              log(
                "Preserving optimistic access state; child reported empty resources",
                {
                  previousSecurityStep: prevState.securityStep,
                  authStatus,
                }
              );

              const nextState = {
                authStatus,
                subscriptions,
                resources: prevState.resources,
                ownedTeams,
                securityStep: prevState.securityStep,
                hasAccess: prevState.hasAccess,
              };

              onStateChange?.(nextState);

              return nextState;
            }

            log("Updating unified state from PAYWALL_STATE_UPDATED", {
              previousSecurityStep: prevState.securityStep,
              nextSecurityStep: newSecurityStep,
              authStatus,
              hasAccess,
            });

            const nextState = {
              authStatus,
              subscriptions,
              resources,
              ownedTeams,
              securityStep: newSecurityStep,
              hasAccess,
            };

            onStateChange?.(nextState);

            return nextState;
          });
        }
      }
    });

    return unsubscribe;
  }, [onMessage, hasAccessClbck, debug, onStateChange]);

  useEffect(() => {
    // AIDEV-NOTE: Initial state can change later
    if (hasUnifiedStateRef.current || !initialState) return;
    setState(() => {
      onStateChange?.(initialState);
      return initialState;
    });
  }, [initialState, onStateChange]);
  // const paywall = usePaywallIframeManager();

  // Update the context with unified state
  const contextValue: PaywallStateManagerContextValue = React.useMemo(
    () => ({
      securityStep: state.securityStep,
      authStatus: state.authStatus,
      subscriptions: state.subscriptions,
      resources: state.resources,
      ownedTeams: state.ownedTeams,
      hasAccess: state.hasAccess,
    }),
    [state]
  );

  return (
    <PaywallStateManagerContext.Provider value={state}>
      {children}
    </PaywallStateManagerContext.Provider>
  );
}

function hasAccessLevelChanged(
  prevState: PaywallStateManagerContextValue,
  subscriptions: SubscriptionDetails[]
) {
  return (
    prevState.subscriptions.map((sub) => sub.mode).join(",") !==
    subscriptions.map((sub) => sub.mode).join(",")
  );
}
